// Filename: src/App.js
//----------------------------------------
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GIF from 'gif.js';
import MapComponent from './components/MapComponent';
import TopBar from './components/TopBar';
import AboutPopup from './components/AboutPopup';
import ImageList from './components/ImageList';
import ThumbnailBar from './components/ThumbnailBar';
import PoweredByBar from './components/PoweredByBar';
import FirstVisitPopup from './components/FirstVisitPopup'; // Import FirstVisitPopup
import AuthModal from './components/AuthModal';
import CreditModal from './components/CreditModal';
import { fetchCreditBalance, deductCredits, createCheckoutSession } from './services/creditsApi';
import { savePlace } from './services/historyApi';
import { apiConfig } from './config/apiConfig'; // Import API configuration
import Analytics from './services/analytics'; // Import Analytics service
import { logFrontendEvent } from './services/apiLogger';
import { createSessionHeaders } from './utils/sessionManager'; // Import session manager
import {
  getAuthUser,
  getAuthorizationHeader,
  isAuthConfigured,
  isEmailAuthConfigured,
  isAuthenticated,
  refreshTokensIfNeeded,
  setStoredEmail,
  startEmailAuth,
  logout,
  startLogin,
  verifyEmailAuth,
  getStoredEmail
} from './services/auth';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.css';
import './components/TopBar.css';
import { isImageBlack } from './utils/imageQuality';

const IMAGE_BATCH_SIZE = 100;
const CLOUD_THRESHOLDS = [50, 10, null]; // Cloud coverage thresholds: <50%, <10%, all images
const CLOUD_THRESHOLD_LABELS = [
  'Hide cloudy images',    // Currently showing <50% clouds
  'Show all images',       // Currently showing <10% clouds
  'Show less cloudy',      // Currently showing all images
];
const GIF_WORKER_URL = `${process.env.PUBLIC_URL || ''}/gif.worker.js`;
const PENDING_MAGIC_ZOOM_KEY = 'stm_magic_zoom_pending';
const PENDING_UI_STATE_KEY = 'stm_ui_resume';

const readPendingMagicZoom = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(PENDING_MAGIC_ZOOM_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Failed to read pending magic zoom state:', error);
    return null;
  }
};

const writePendingMagicZoom = (value) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    if (!value) {
      window.sessionStorage.removeItem(PENDING_MAGIC_ZOOM_KEY);
      return;
    }
    window.sessionStorage.setItem(PENDING_MAGIC_ZOOM_KEY, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to persist pending magic zoom state:', error);
  }
};

const readPendingUiState = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(PENDING_UI_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Failed to read pending UI state:', error);
    return null;
  }
};

const writePendingUiState = (value) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    if (!value) {
      window.sessionStorage.removeItem(PENDING_UI_STATE_KEY);
      return;
    }
    window.sessionStorage.setItem(PENDING_UI_STATE_KEY, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to persist pending UI state:', error);
  }
};

const App = () => {
  const [showAbout, setShowAbout] = useState(false);
  const [bbox, setBbox] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);
  const [showRedBox, setShowRedBox] = useState(true);
  const [images, setImages] = useState([]);
  const [selectedImagePaths, setSelectedImagePaths] = useState(() => new Set());
  const [allImages, setAllImages] = useState([]); // Store all images
  const [loadingImageBatch, setLoadingImageBatch] = useState(false);
  const [mode, setMode] = useState('map'); // modes: 'map', 'imagelist', 'imageview'
  const [initialBbox, setInitialBbox] = useState(null);
  const [initialCenter, setInitialCenter] = useState(null);
  const [initialZoom, setInitialZoom] = useState(null);
  const [magicZoomQueue, setMagicZoomQueue] = useState([]);
  const [activeMagicZoom, setActiveMagicZoom] = useState(null);
  const [magicZoomProgress, setMagicZoomProgress] = useState(0);
  const [magicZoomMessage, setMagicZoomMessage] = useState('');
  const [magicZoomBatchTotal, setMagicZoomBatchTotal] = useState(0);
  const [isGifGenerating, setIsGifGenerating] = useState(false);
  const [gifProgress, setGifProgress] = useState(0);
  const [cloudFilterIndex, setCloudFilterIndex] = useState(0); // Initialize to show <50% clouds
  const [boxDimensions, setBoxDimensions] = useState({ width: 500, height: 500 }); // Store box dimensions
  const [showFirstVisitPopup, setShowFirstVisitPopup] = useState(false); // State for FirstVisitPopup
  const [authUser, setAuthUser] = useState(() => getAuthUser());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authEmail, setAuthEmail] = useState(() => getStoredEmail());
  const [authCode, setAuthCode] = useState('');
  const [authSession, setAuthSession] = useState(null);
  const [authStep, setAuthStep] = useState('email');
  const [authLoading, setAuthLoading] = useState(false);
  const [pendingMagicZoom, setPendingMagicZoom] = useState(() => readPendingMagicZoom());
  const [pendingUiState, setPendingUiState] = useState(() => readPendingUiState());
  const [creditBalance, setCreditBalance] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditModalRequired, setCreditModalRequired] = useState(1);
  const [creditModalLoading, setCreditModalLoading] = useState(false);
  const [creditModalError, setCreditModalError] = useState('');
  const [pendingCreditAction, setPendingCreditAction] = useState(null);
  const [prefetchedImages, setPrefetchedImages] = useState(null); // Store prefetched dates
  const [mapMoved, setMapMoved] = useState(false); // Track if user moved the map
  const [showSearch, setShowSearch] = useState(true); // Show search on first visit
  const [searchQuery, setSearchQuery] = useState(''); // Search input value
  const [updateBboxFn, setUpdateBboxFn] = useState(null); // Store the bbox update function
  const [imageViewMode, setImageViewMode] = useState('natural'); // Cycle through: 'natural' -> 'evi' -> 'false_color'
  const [tileMode, setTileMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false); // Search loading state
  const [searchSuggestions, setSearchSuggestions] = useState([]); // Search suggestions
  const [showSuggestions, setShowSuggestions] = useState(false); // Show/hide suggestions
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1); // Keyboard navigation
  const [searchTimeout, setSearchTimeout] = useState(null); // Debounce search
  const getMapStateFnRef = useRef(null); // Store map state getter function
  
  const handleMapMove = useCallback(() => {
    setMapMoved(true);
  }, []);

  const handleSetMapStateFn = useCallback((fn) => {
    getMapStateFnRef.current = fn;
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const cloudThresholds = CLOUD_THRESHOLDS;
  const cloudThresholdLabels = CLOUD_THRESHOLD_LABELS;
  const authConfigured = isAuthConfigured();
  const emailAuthConfigured = isEmailAuthConfigured();
  const googleAuthConfigured = Boolean(apiConfig?.auth?.googleEnabled);
  const storePendingMagicZoom = useCallback((value) => {
    setPendingMagicZoom(value);
    writePendingMagicZoom(value);
  }, []);
  const clearPendingMagicZoom = useCallback(() => {
    setPendingMagicZoom(null);
    writePendingMagicZoom(null);
  }, []);
  const storePendingUiState = useCallback((value) => {
    setPendingUiState(value);
    writePendingUiState(value);
  }, []);
  const clearPendingUiState = useCallback(() => {
    setPendingUiState(null);
    writePendingUiState(null);
  }, []);

  const buildPendingMagicZoomImage = useCallback(
    (image) => {
      if (!image) {
        return null;
      }
      const pendingBbox = image.bbox || bbox;
      if (!pendingBbox || pendingBbox.length !== 4) {
        return null;
      }
      return {
        ...image,
        bbox: pendingBbox,
        mgrs_tile: image.mgrs_tile || '',
        stacked_tif_s3_path: image.stacked_tif_s3_path || ''
      };
    },
    [bbox]
  );

  const queueMagicZoomAfterAuth = useCallback(
    (type, image) => {
      if (!authConfigured) {
        return;
      }
      if (type === 'all') {
        storePendingMagicZoom({ type: 'all' });
        return;
      }
      const pendingImage = buildPendingMagicZoomImage(image);
      if (!pendingImage) {
        return;
      }
      storePendingMagicZoom({ type: 'single', image: pendingImage });
    },
    [authConfigured, buildPendingMagicZoomImage, storePendingMagicZoom]
  );

  const captureUiStateForAuth = useCallback(() => {
    const mapState = getMapStateFnRef.current ? getMapStateFnRef.current() : null;
    storePendingUiState({
      ts: Date.now(),
      mode,
      bbox,
      mapCenter: mapState?.center || mapCenter,
      mapZoom: mapState?.zoom || mapZoom,
      showRedBox,
      showSearch,
      imageViewMode,
      cloudFilterIndex,
      images,
      allImages,
      selectedImage,
      selectedImagePaths: Array.from(selectedImagePaths),
    });
  }, [
    allImages,
    bbox,
    cloudFilterIndex,
    imageViewMode,
    images,
    mapCenter,
    mapZoom,
    mode,
    selectedImage,
    selectedImagePaths,
    showRedBox,
    showSearch,
    storePendingUiState,
  ]);

  const buildPathWithQueryAndHash = useCallback((path, { keepHash = true } = {}) => {
    const search = location.search || '';
    const hash = keepHash ? window.location.hash : '';
    return `${path}${search}${hash}`;
  }, [location.search]);

  useEffect(() => {
    if (location.pathname === '/') {
      navigate(buildPathWithQueryAndHash('/map'));
    }

    // Track session start
    const referrer = document.referrer || 'direct';
    const entryPoint = window.location.pathname + window.location.hash;
    Analytics.trackSessionStarted(entryPoint, referrer);

    // Check for first visit
    const hasVisited = localStorage.getItem('hasVisitedSentimezoom');
    if (!hasVisited || hasVisited !== 'true') {
      setShowFirstVisitPopup(true);
      localStorage.setItem('hasVisitedSentimezoom', 'true');
      Analytics.trackFirstVisitPopup('shown');
    }
  }, [buildPathWithQueryAndHash, location, navigate]);

  // SR tasks from history navigation — applied after images finish loading
  const [historySrTasks, setHistorySrTasks] = useState(null);
  // Flag: auto-transition to imageview after images load from history
  const historyAutoShowMap = useRef(false);

  // Handle navigation from /history page: auto-load images for a saved place
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hbbox = params.get('hbbox');
    if (!hbbox) return;

    const parts = hbbox.split(',').map(Number);
    if (parts.length !== 4 || parts.some(isNaN)) return;

    // Remove param to avoid re-triggering
    params.delete('hbbox');
    const cleanSearch = params.toString();
    const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', cleanUrl);

    // Read SR tasks stored by HistoryPage
    try {
      const srData = sessionStorage.getItem('history_sr_tasks');
      if (srData) {
        setHistorySrTasks(JSON.parse(srData));
        sessionStorage.removeItem('history_sr_tasks');
      }
    } catch (e) { /* ignore */ }

    // Set bbox, enter imagelist temporarily (will auto-transition to imageview)
    setBbox(parts);
    setMode('imagelist');
    setShowRedBox(false);
    setShowSearch(false);
    setPrefetchedImages(null);
    historyAutoShowMap.current = true;

    // Fetch dates for this bbox and load images directly
    const bboxString = parts.join(',');
    const year = new Date().getFullYear();
    fetch(`${apiConfig.getDates}?bbox=${bboxString}&year=${year}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          updateImages(data.map(img => ({ ...img, loading: true })));
        }
      })
      .catch(err => console.error('History image load failed:', err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      setAuthUser(getAuthUser());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    let active = true;
    refreshTokensIfNeeded()
      .then(() => {
        if (active) {
          setAuthUser(getAuthUser());
        }
      })
      .catch(() => {
        if (active) {
          setAuthUser(null);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!pendingUiState) {
      return;
    }
    if (!authUser) {
      return;
    }
    if (pendingUiState.ts && Date.now() - pendingUiState.ts > 30 * 60 * 1000) {
      clearPendingUiState();
      return;
    }

    if (pendingUiState.bbox) {
      setBbox(pendingUiState.bbox);
    }
    if (pendingUiState.mapCenter) {
      setMapCenter(pendingUiState.mapCenter);
    }
    if (pendingUiState.mapZoom) {
      setMapZoom(pendingUiState.mapZoom);
    }

    setMode(pendingUiState.mode || 'map');
    setShowRedBox(pendingUiState.showRedBox ?? true);
    setShowSearch(pendingUiState.showSearch ?? true);
    setImageViewMode(pendingUiState.imageViewMode || 'natural');
    if (typeof pendingUiState.cloudFilterIndex === 'number') {
      setCloudFilterIndex(pendingUiState.cloudFilterIndex);
    }

    if (Array.isArray(pendingUiState.images)) {
      setImages(pendingUiState.images);
    }
    if (Array.isArray(pendingUiState.allImages)) {
      setAllImages(pendingUiState.allImages);
    }
    if (Array.isArray(pendingUiState.selectedImagePaths)) {
      setSelectedImagePaths(new Set(pendingUiState.selectedImagePaths));
    }
    if (pendingUiState.selectedImage) {
      setSelectedImage(pendingUiState.selectedImage);
      setInitialBbox(pendingUiState.selectedImage.bbox || pendingUiState.bbox || null);
      setInitialCenter(
        pendingUiState.selectedImage.center || pendingUiState.mapCenter || null
      );
      setInitialZoom(
        pendingUiState.selectedImage.zoom || pendingUiState.mapZoom || null
      );
    }

    if (pendingUiState.mode === 'imagelist' || pendingUiState.mode === 'imageview') {
      setShowRedBox(false);
      setShowSearch(false);
    }

    clearPendingUiState();
  }, [authUser, clearPendingUiState, pendingUiState]);


  const handleCloseFirstVisitPopup = () => { // Handler to close FirstVisitPopup
    Analytics.trackFirstVisitPopup('dismissed');
    setShowFirstVisitPopup(false);
  };

  // Fetch credit balance when user logs in
  useEffect(() => {
    if (!authUser) {
      setCreditBalance(null);
      return;
    }
    let cancelled = false;
    fetchCreditBalance()
      .then((data) => {
        if (!cancelled) setCreditBalance(data);
      })
      .catch((err) => {
        console.warn('Failed to fetch credit balance:', err);
      });
    return () => { cancelled = true; };
  }, [authUser]);

  // Deduct 1 credit after a successful SR image (fire-and-forget)
  const deductOneCredit = useCallback(() => {
    deductCredits(1)
      .then((data) => setCreditBalance(data))
      .catch((err) => {
        console.warn('Post-SR credit deduction failed:', err);
        // Refresh balance to stay in sync
        fetchCreditBalance()
          .then((data) => setCreditBalance(data))
          .catch(() => {});
      });
  }, []);

  const requireCredits = useCallback((count, onSuccess) => {
    // Check balance from local state — no deduction yet (deduction happens after SR success)
    const total = creditBalance?.total_credits ?? 0;
    if (total >= count) {
      onSuccess();
      return;
    }
    // Not enough — refresh balance from server then show modal
    fetchCreditBalance()
      .then((data) => {
        setCreditBalance(data);
        if (data.total_credits >= count) {
          onSuccess();
        } else {
          setCreditModalRequired(count);
          setCreditModalError('');
          setPendingCreditAction(() => onSuccess);
          setShowCreditModal(true);
        }
      })
      .catch((err) => {
        // Server error — fail open for <10 users validation phase
        console.warn('Credits check error, allowing SR:', err);
        onSuccess();
      });
  }, [creditBalance]);

  const handleCreditModalProceed = useCallback(() => {
    // User clicked proceed in modal — they now have enough credits, just start SR
    setShowCreditModal(false);
    setCreditModalLoading(false);
    if (pendingCreditAction) pendingCreditAction();
    setPendingCreditAction(null);
  }, [pendingCreditAction]);

  const handleBuyCredits = useCallback(() => {
    setCreditModalLoading(true);
    setCreditModalError('');
    createCheckoutSession()
      .then((data) => {
        localStorage.setItem('stripe_return_path', window.location.pathname + window.location.hash);
        window.location.href = data.url;
      })
      .catch((err) => {
        setCreditModalLoading(false);
        setCreditModalError(err.message || 'Failed to start checkout.');
      });
  }, []);

  const handleCreditModalClose = useCallback(() => {
    setShowCreditModal(false);
    setCreditModalError('');
    setPendingCreditAction(null);
  }, []);

  const requireMagicZoomAuth = useCallback(() => {
    if (!authConfigured) {
      setAuthError('Auth is not configured yet.');
      setShowAuthModal(true);
      return false;
    }
    if (!isAuthenticated()) {
      setAuthUser(null);
      setAuthError('');
      setShowAuthModal(true);
      return false;
    }
    return true;
  }, [authConfigured]);

  const handleAuthStart = useCallback(
    async (provider) => {
      try {
        const returnPath = `${location.pathname}${location.search}${location.hash}`;
        captureUiStateForAuth();
        await startLogin({ provider, returnPath });
      } catch (error) {
        setAuthError(error.message || 'Failed to start sign-in.');
      }
    },
    [captureUiStateForAuth, location.hash, location.pathname, location.search]
  );

  const handleEmailAuthStart = useCallback(async () => {
    const normalizedEmail = authEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      setAuthError('Please enter a valid email.');
      return;
    }
    if (!emailAuthConfigured) {
      setAuthError('Email sign-in is not configured yet.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      setAuthEmail(normalizedEmail);
      const response = await startEmailAuth(normalizedEmail);
      setStoredEmail(normalizedEmail);
      setAuthSession(response.session || null);
      setAuthStep('code');
    } catch (error) {
      setAuthError(error.message || 'Failed to send code.');
    } finally {
      setAuthLoading(false);
    }
  }, [authEmail, emailAuthConfigured]);

  const handleEmailAuthVerify = useCallback(async () => {
    if (!authSession) {
      setAuthError('Please request a new code.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      const normalizedCode = authCode.trim();
      await verifyEmailAuth({ email: authEmail, code: normalizedCode, session: authSession });
      setAuthUser(getAuthUser());
      setShowAuthModal(false);
      setAuthCode('');
      setAuthSession(null);
      setAuthStep('email');
    } catch (error) {
      setAuthError(error.message || 'Invalid code. Try again.');
    } finally {
      setAuthLoading(false);
    }
  }, [authCode, authEmail, authSession]);

  const handleEmailAuthReset = useCallback(() => {
    setAuthCode('');
    setAuthSession(null);
    setAuthStep('email');
  }, []);

  const handleSignOut = useCallback(() => {
    logout();
    setAuthUser(null);
    setAuthCode('');
    setAuthSession(null);
    setAuthStep('email');
  }, []);

  const handleAboutClick = () => setShowAbout(true);
  const handleCloseAbout = () => setShowAbout(false);

  const handleToggleImageSelection = useCallback((image) => {
    setSelectedImagePaths((prevPaths) => {
      const nextPaths = new Set(prevPaths);
      if (nextPaths.has(image.path)) {
        nextPaths.delete(image.path);
      } else {
        nextPaths.add(image.path);
      }
      return nextPaths;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedImagePaths(new Set());
  }, []);

  const pickLatestImage = useCallback((imageList) => {
    if (!imageList || imageList.length === 0) {
      return null;
    }
    const sortedByDate = [...imageList].sort((a, b) => new Date(b.date) - new Date(a.date));
    return (
      sortedByDate.find((img) => img.url && !img.loading) ||
      sortedByDate.find((img) => img.url) ||
      sortedByDate[0]
    );
  }, []);

  const selectedImages = useMemo(() => {
    const sourceImages = allImages.length > 0 ? allImages : images;
    return sourceImages.filter((img) => selectedImagePaths.has(img.path));
  }, [allImages, images, selectedImagePaths]);

  const hasSelection = selectedImagePaths.size > 0;

  const handleImageClick = (image) => {
    // Check if mobile device
    const isMobile = window.innerWidth <= 768;
    const defaultZoom = isMobile ? 14.5 : 15.6;
    
    console.log('Image clicked:', {
      has_false_color_url: !!image.false_color_url,
      false_color_url: image.false_color_url,
      has_evi_url: !!image.evi_url,
      evi_url: image.evi_url
    });
    
    // Get current map state instead of using potentially outdated React state
    const currentMapState = getMapStateFnRef.current ? getMapStateFnRef.current() : null;
    const currentCenter = currentMapState ? currentMapState.center : mapCenter;
    
    const selectedImageData = {
      ...image,
      bbox: bbox,
      center: currentCenter,
      zoom: defaultZoom, // Set zoom level based on device
    };

    if (
      !selectedImageData.bbox ||
      !selectedImageData.url ||
      !selectedImageData.center ||
      !selectedImageData.zoom
    ) {
      console.error('Selected image data is incomplete:', selectedImageData);
      return;
    }

    setSelectedImage(selectedImageData);
    setInitialBbox(bbox);
    setInitialCenter(currentCenter);
    setInitialZoom(defaultZoom);
    setMode('imageview');
    navigate(buildPathWithQueryAndHash('/map'));
  };

  const handleButtonClick = () => {
    if (mode === 'map') {
      // User clicks "Show Sentinel-2 images"
      // Update bbox before switching modes
      if (updateBboxFn) {
        updateBboxFn();
      }
      
      if (!mapMoved && prefetchedImages) {
        updateImages(prefetchedImages);
        setPrefetchedImages(null);
      }
      setMode('imagelist');
      // Save place to history (fire-and-forget, non-blocking)
      savePlace({
        lat: mapCenter?.lat,
        lng: mapCenter?.lng,
        zoom: mapZoom,
        bbox: bbox,
        location_name: searchQuery || '',
      });
      setShowRedBox(false);
      setShowSearch(false); // Hide search when showing images
      // Reset to Natural color when switching to image list
      setImageViewMode('natural');
      navigate(buildPathWithQueryAndHash('/imagelist'));
    } else if (mode === 'imagelist') {
      // User clicks "Show on the map"
      const imagesForMap = hasSelection && selectedImages.length > 0 ? selectedImages : images;
      const latestImage = pickLatestImage(imagesForMap);
      if (latestImage) {
        handleImageClick(latestImage);
      }
    } else if (mode === 'imageview') {
      // User clicks "New place"
      setMode('map');
      setSelectedImage(null);
      setShowRedBox(true);
      setImages([]);
      setAllImages([]); // Reset all images
      setSelectedImagePaths(new Set());
      setMagicZoomQueue([]);
      setActiveMagicZoom(null);
      setMagicZoomProgress(0);
      setMagicZoomMessage('');
      setIsGifGenerating(false);
      setGifProgress(0);
      setCloudFilterIndex(0); // Reset cloud filter index to show <50% clouds
      setShowSearch(true); // Show search when going to new place
      setSearchQuery(''); // Clear search query
      // Reset to Natural color when going to new place
      setImageViewMode('natural');
      navigate(buildPathWithQueryAndHash('/map', { keepHash: false })); // Don't use hash to avoid moving map to stored coordinates
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchLoading) return;
    
    setSearchLoading(true);
    try {
      // Use Mapbox Geocoding API to search for the location
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=pk.eyJ1IjoidmFyaWsiLCJhIjoiY2tlZGQxejhnMGEzczJ6a2ZjdnMxcGo5NyJ9.jCdGWqxlTtpyc_PV0qnuPA&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;
        
        console.log(`Search result: ${feature.place_name} at [${lng}, ${lat}]`);
        
        // Track search event
        Analytics.trackLocationSearched(searchQuery, feature.place_name, { lat, lng });
        
        // Update map center and zoom
        setMapCenter({ lng, lat });
        setMapZoom(15); // Reasonable zoom level for searched locations
        
        // Clear search query but keep search visible
        setSearchQuery('');
      } else {
        console.log('No search results found');
        // Track failed search
        Analytics.trackLocationSearched(searchQuery, null, null);
      }
    } catch (error) {
      console.error('Search error:', error);
      // Could add user feedback here in the future
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchSearchSuggestions = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=pk.eyJ1IjoidmFyaWsiLCJhIjoiY2tlZGQxejhnMGEzczJ6a2ZjdnMxcGo5NyJ9.jCdGWqxlTtpyc_PV0qnuPA&limit=20&autocomplete=true`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        setSearchSuggestions(data.features);
        setShowSuggestions(true);
        setSelectedSuggestionIndex(-1);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Suggestions error:', error);
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    const [lng, lat] = suggestion.center;
    console.log(`Selected suggestion: ${suggestion.place_name} at [${lng}, ${lat}]`);
    
    // Track suggestion selection
    Analytics.trackLocationSearched(searchQuery, suggestion.place_name, { lat, lng });
    
    // Update map center and zoom
    setMapCenter({ lng, lat });
    setMapZoom(15);
    
    // Clear search and hide suggestions
    setSearchQuery('');
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || searchSuggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < searchSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionSelect(searchSuggestions[selectedSuggestionIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        // No action needed for other keys
        break;
    }
  };

  const handleToggleImageView = () => {
    console.log('Toggling image view mode');
    // Cycle through: Natural Color -> EVI -> False Color -> Natural Color
    const modes = ['natural', 'evi', 'false_color'];
    const currentIndex = modes.indexOf(imageViewMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    const previousMode = imageViewMode;
    const newMode = modes[nextIndex];
    
    // Track view mode change
    Analytics.trackViewModeChanged(newMode, previousMode, mode === 'imagelist' ? 'image_list' : 'map_view');
    
    setImageViewMode(newMode);
    console.log(`Image view mode changed from ${previousMode} to ${newMode}`);
  };

  const handleToggleTileMode = useCallback(() => {
    setTileMode(prev => !prev);
  }, []);

  const handleDateChange = (image) => {
    console.log('handleDateChange called with image:', image.date);
    
    // Get current map state to preserve current view when switching images
    const currentMapState = getMapStateFnRef.current ? getMapStateFnRef.current() : null;
    const currentCenter = currentMapState ? currentMapState.center : (initialCenter || mapCenter);
    const currentZoom = currentMapState ? currentMapState.zoom : (selectedImage?.zoom || initialZoom || mapZoom);
    
    const selectedImageData = {
      ...image,
      bbox: initialBbox || bbox,
      center: currentCenter,  // Use current map center
      zoom: currentZoom,      // Use current map zoom
      lastUpdated: Date.now(), // Force re-render by adding timestamp for hover events
    };
    console.log('Setting selected image to:', selectedImageData);
    setSelectedImage(selectedImageData);
  };

  const handleViewImageListClick = () => {
    if (mode === 'imageview') {
      // Re-apply current filter when switching back to image list
      const threshold = cloudThresholds[cloudFilterIndex];

      if (threshold === null) {
        // Show all images except black ones
        const filteredImages = allImages.filter((img) => !isImageBlack(img));
        setImages(filteredImages);
      } else {
        // Filter images - only include those with cloud percentage <= threshold AND not black
        const filteredImages = allImages.filter((img) => {
          // First check if image is black - always exclude black images
          if (isImageBlack(img)) {
            return false;
          }

          if (img.percentages && img.percentages.cloud_percentage !== undefined) {
            return img.percentages.cloud_percentage <= threshold;
          }
          // If cloud_percentage is not available, exclude the image (assume it's cloudy)
          return false;
        });
        setImages(filteredImages);
      }

      setMode('imagelist');
      navigate(buildPathWithQueryAndHash('/imagelist'));
    }
  };

  const handleRemoveCloudyImagesClick = () => {
    const nextIndex = (cloudFilterIndex + 1) % cloudThresholds.length;
    setCloudFilterIndex(nextIndex);

    const threshold = cloudThresholds[nextIndex];
    const filterType = cloudThresholdLabels[nextIndex];
    
    // Track cloud filter applied
    if (bbox) {
      const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
      Analytics.trackCloudFilterApplied(filterType, `${center[1]},${center[0]}`);
    }

    if (threshold === null) {
      // Show all images except black ones
      const filteredImages = allImages.filter((img) => !isImageBlack(img));
      setImages(filteredImages);
    } else {
      // Filter images - only include those with cloud percentage <= threshold AND not black
      const filteredImages = allImages.filter((img) => {
        // First check if image is black - always exclude black images
        if (isImageBlack(img)) {
          return false;
        }
        
        if (img.percentages && img.percentages.cloud_percentage !== undefined) {
          return img.percentages.cloud_percentage <= threshold;
        }
        // If cloud_percentage is not available, exclude the image (assume it's cloudy)
        return false;
      });
      setImages(filteredImages);
    }
  };

  const getImageUrlForMode = useCallback((image) => {
    if (!image) {
      return null;
    }
    if (imageViewMode === 'evi' && image.evi_url) {
      return image.evi_url;
    }
    if (imageViewMode === 'false_color' && image.false_color_url) {
      return image.false_color_url;
    }
    return image.url;
  }, [imageViewMode]);

  const resolveLatLon = useCallback(
    (image) => {
      if (Number.isFinite(image?.latitude) && Number.isFinite(image?.longitude)) {
        return { lat: image.latitude, lon: image.longitude };
      }
      if (bbox && bbox.length === 4) {
        return {
          lat: (bbox[1] + bbox[3]) / 2,
          lon: (bbox[0] + bbox[2]) / 2
        };
      }
      if (mapCenter) {
        return { lat: mapCenter.lat, lon: mapCenter.lng };
      }
      return null;
    },
    [bbox, mapCenter]
  );

  const mapButtonText = hasSelection ? 'Show selected on the map' : 'Show All on the map';

  const buttonText =
    mode === 'map'
      ? 'Show Sentinel-2 images'
      : mode === 'imagelist'
      ? mapButtonText
      : 'New place';

  const hasImages = images.length > 0;
  const zoomAllEnabled = useMemo(() => {
    // Show AI Zoom All for all authenticated users (credits gate usage)
    if (isAuthenticated()) {
      return true;
    }
    const searchParams = new URLSearchParams(location.search || '');
    if (searchParams.has('zoom_all')) {
      return true;
    }
    const hashQueryIndex = location.hash ? location.hash.indexOf('?') : -1;
    if (hashQueryIndex >= 0) {
      const hashParams = new URLSearchParams(location.hash.slice(hashQueryIndex));
      return hashParams.has('zoom_all');
    }
    return false;
  }, [location.hash, location.search, authUser]); // eslint-disable-line react-hooks/exhaustive-deps
  const magicZoomQueueCount = magicZoomQueue.length;
  const showMagicZoomBar = Boolean(activeMagicZoom || magicZoomQueueCount > 0);
  const magicZoomActiveDate = activeMagicZoom
    ? new Date(activeMagicZoom.image.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : null;
  const magicZoomNextDate =
    !activeMagicZoom && magicZoomQueueCount > 0
      ? new Date(magicZoomQueue[0].image.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : null;
  const magicZoomDisplayProgress = useMemo(() => {
    if (!magicZoomBatchTotal) {
      return magicZoomProgress;
    }
    const remaining = magicZoomQueueCount + (activeMagicZoom ? 1 : 0);
    const completed = Math.max(0, magicZoomBatchTotal - remaining);
    const activeFraction = activeMagicZoom
      ? Math.min(1, Math.max(0, magicZoomProgress / 100))
      : 0;
    const overall = ((completed + activeFraction) / magicZoomBatchTotal) * 100;
    return Math.min(100, Math.max(0, overall));
  }, [activeMagicZoom, magicZoomBatchTotal, magicZoomProgress, magicZoomQueueCount]);

  const timelineImages =
    hasSelection && selectedImages.length > 0 ? selectedImages : images;

  const hasGifFrames = useMemo(
    () => timelineImages.some((image) => !image.loading && getImageUrlForMode(image)),
    [getImageUrlForMode, timelineImages]
  );

  // Updated updateImages function

  // Deduplicate images sharing the same date: keep the one with lowest black_percentage (+ cloud_percentage tiebreaker)
  const deduplicateByDate = (imageArray) => {
    const dateMap = new Map();
    imageArray.forEach((img) => {
      const dateKey = img.date; // e.g. "2025-06-15"
      const existing = dateMap.get(dateKey);
      if (!existing) {
        dateMap.set(dateKey, img);
        return;
      }
      // Both must be loaded (not loading) to compare; prefer loaded over loading
      if (existing.loading && !img.loading) {
        dateMap.set(dateKey, img);
        return;
      }
      if (!existing.loading && img.loading) {
        return;
      }
      // Compare: lower black_percentage wins; tiebreak on cloud_percentage
      const existBlack = existing.black_percentage ?? (existing.is_black ? 100 : 0);
      const newBlack = img.black_percentage ?? (img.is_black ? 100 : 0);
      if (newBlack < existBlack) {
        dateMap.set(dateKey, img);
      } else if (newBlack === existBlack) {
        const existCloud = existing.percentages?.cloud_percentage ?? 100;
        const newCloud = img.percentages?.cloud_percentage ?? 100;
        if (newCloud < existCloud) {
          dateMap.set(dateKey, img);
        }
      }
    });
    return Array.from(dateMap.values());
  };

  const updateImages = useCallback((newImages) => {
    const preserveSuperresolved = (existingImg, updatedImg) => {
      if (!existingImg?.superresolved || updatedImg?.superresolved) {
        return updatedImg;
      }
      return {
        ...updatedImg,
        superresolved: true,
        url: existingImg.url,
        false_color_url: existingImg.false_color_url,
        evi_url: existingImg.evi_url,
        original_url: existingImg.original_url,
        original_false_color_url: existingImg.original_false_color_url,
        original_evi_url: existingImg.original_evi_url,
        superres_tif_url: existingImg.superres_tif_url,
      };
    };

    // First, prepare the updated allImages data
    let updatedAllImages;
    setAllImages((prevAllImages) => {
      const imageMap = new Map();
      // Add previous images to the map
      prevAllImages.forEach((img) => {
        imageMap.set(img.path, img);
      });
      // Update the map with new images
      newImages.forEach((img) => {
        const existingImg = imageMap.get(img.path) || {};
        // Don't overwrite loaded images with loading state - preserve URLs and loaded state
        if (existingImg.url && !existingImg.loading && img.loading) {
          // Keep existing loaded image, only update metadata if needed
          const updatedImg = {
            ...img,
            url: existingImg.url,
            false_color_url: existingImg.false_color_url,
            evi_url: existingImg.evi_url,
            percentages: existingImg.percentages,
            stacked_tif_s3_path: existingImg.stacked_tif_s3_path,
            is_black: existingImg.is_black,
            black_percentage: existingImg.black_percentage,
            original_url: existingImg.original_url,
            original_false_color_url: existingImg.original_false_color_url,
            original_evi_url: existingImg.original_evi_url,
            superresolved: existingImg.superresolved,
            superres_tif_url: existingImg.superres_tif_url,
            loading: false
          };
          imageMap.set(img.path, preserveSuperresolved(existingImg, updatedImg));
        } else {
          const updatedImg = preserveSuperresolved(existingImg, { ...existingImg, ...img });
          imageMap.set(img.path, updatedImg);
        }
      });
      updatedAllImages = deduplicateByDate(Array.from(imageMap.values()));
      return updatedAllImages;
    });

    // Apply current filter
    const threshold = cloudThresholds[cloudFilterIndex];

    setImages((prevImages) => {
      const imageMap = new Map();
      // Add previous images to the map
      prevImages.forEach((img) => {
        imageMap.set(img.path, img);
      });
      // Update the map with new images
      newImages.forEach((img) => {
        const existingImg = imageMap.get(img.path) || {};
        let updatedImg;
        
        // Don't overwrite loaded images with loading state - preserve URLs and loaded state
        if (existingImg.url && !existingImg.loading && img.loading) {
          // Keep existing loaded image, only update metadata if needed
          updatedImg = {
            ...img,
            url: existingImg.url,
            false_color_url: existingImg.false_color_url,
            evi_url: existingImg.evi_url,
            percentages: existingImg.percentages,
            stacked_tif_s3_path: existingImg.stacked_tif_s3_path,
            is_black: existingImg.is_black,
            black_percentage: existingImg.black_percentage,
            original_url: existingImg.original_url,
            original_false_color_url: existingImg.original_false_color_url,
            original_evi_url: existingImg.original_evi_url,
            superresolved: existingImg.superresolved,
            superres_tif_url: existingImg.superres_tif_url,
            loading: false
          };
        } else {
          updatedImg = { ...existingImg, ...img };
        }
        updatedImg = preserveSuperresolved(existingImg, updatedImg);

        // Apply filter based on threshold
        if (threshold === null) {
          // Show all images except black ones
          if (!isImageBlack(updatedImg)) {
            imageMap.set(img.path, updatedImg);
          }
          // Skip black images entirely
        } else if (updatedImg.loading) {
          // For loading images, check if we have cloud data from allImages to decide if we should load
          const allImageData = updatedAllImages?.find(allImg => allImg.path === img.path);
          if (allImageData && allImageData.percentages && allImageData.percentages.cloud_percentage !== undefined) {
            // We have cloud data, apply filter
            if (allImageData.percentages.cloud_percentage <= threshold && !isImageBlack(allImageData)) {
              imageMap.set(img.path, updatedImg);
            }
            // Don't add images that exceed cloud threshold or are black - skip loading them
          } else {
            // No cloud data available, but check if we know it's black
            if (allImageData && isImageBlack(allImageData)) {
              // Skip black images even when loading
            } else {
              // Keep loading to determine cloud/black status
              imageMap.set(img.path, updatedImg);
            }
          }
        } else if (
          updatedImg.percentages &&
          updatedImg.percentages.cloud_percentage !== undefined &&
          updatedImg.percentages.cloud_percentage <= threshold &&
          !isImageBlack(updatedImg)
        ) {
          // Keep images with cloud percentage <= threshold AND not black
          imageMap.set(img.path, updatedImg);
        } else {
          // Remove images that don't meet the criteria
          imageMap.delete(img.path);
        }
      });
      const updatedImages = deduplicateByDate(Array.from(imageMap.values()));

      return updatedImages;
    });
  }, [cloudFilterIndex, cloudThresholds]);

  const fetchImageBatch = useCallback(
    async (imagesToFetch, bboxParam = bbox) => {
      if (!bboxParam || bboxParam.length !== 4) {
        return;
      }
      setLoadingImageBatch(true);
      const fetchPromises = imagesToFetch.map(async (image) => {
        try {
          const bboxString = bboxParam.join(',');
          let apiUrl = `${apiConfig.getS2Image}?bbox=${bboxString}&s3_path=${image.path}`;
          apiUrl += '&calcClouds=true';
          const startTime = Date.now();
          const response = await fetch(apiUrl, {
            credentials: 'include',
            headers: {
              ...createSessionHeaders(),
              'X-GA-Cookies': document.cookie
            }
          });
          const loadTime = Date.now() - startTime;

          if (loadTime > 3000) {
            Analytics.trackSlowLoad('satellite_image', loadTime, apiUrl);
          }

          const data = await response.json();
          return {
            ...image,
            url: data.image_link,
            false_color_url: data.false_color_link,
            false_color_s3_path: data.false_color_s3_path,
            evi_url: data.evi_link,
            evi_s3_path: data.evi_s3_path,
            cog_tci_url: data.cog_tci_url || null,
            percentages: data.percentages,
            stacked_tif_s3_path: data.stacked_tif_s3_path,
            is_black: data.is_black,
            black_percentage: data.black_percentage,
            loading: false,
          };
        } catch (error) {
          console.error('Error fetching image:', error);
          Analytics.trackApiError('getS2Image', error.message || 'fetch_failed', `path: ${image.path}`);
          return { ...image, loading: false };
        }
      });

      const fetchedImages = await Promise.all(fetchPromises);
      updateImages(fetchedImages);
      setLoadingImageBatch(false);
    },
    [bbox, updateImages]
  );

  useEffect(() => {
    const imagesToFetch = images.filter((img) => img.loading).slice(0, IMAGE_BATCH_SIZE);
    if (imagesToFetch.length > 0 && !loadingImageBatch) {
      fetchImageBatch(imagesToFetch);
    }
  }, [images, fetchImageBatch, loadingImageBatch]);

  const prefetchDates = useCallback(async (bboxParam) => {
    if (!bboxParam || bboxParam.length !== 4 || prefetchedImages) return;
    try {
      const bboxString = bboxParam.join(',');
      const year = new Date().getFullYear();
      
      // Debug: Log cookies
      console.log('🍪 Document cookies:', document.cookie);
      console.log('🍪 GA cookies:', {
        _ga: document.cookie.split('; ').find(row => row.startsWith('_ga='))?.split('=')[1],
        _gid: document.cookie.split('; ').find(row => row.startsWith('_gid='))?.split('=')[1],
        all_cookies: document.cookie.split('; ')
      });
      
      const response = await fetch(`${apiConfig.getDates}?bbox=${bboxString}&year=${year}`, {
        credentials: 'include',
        headers: {
          'X-GA-Cookies': document.cookie // Send cookies in a custom header as backup
        }
      });
      const data = await response.json();
      
      // Check if data is an array before mapping
      if (Array.isArray(data)) {
        const preImages = data.map((img) => ({ ...img, loading: true }));
        setPrefetchedImages(preImages);
      } else {
        console.error('Unexpected response format from getDates API:', data);
      }
    } catch (err) {
      console.error('Prefetch dates failed:', err);
    }
  }, [prefetchedImages]);

  useEffect(() => {
    if (bbox && !prefetchedImages) {
      prefetchDates(bbox);
    }
  }, [bbox, prefetchedImages, prefetchDates]);

  const normalizeS3Url = useCallback((url) => {
    if (!url) {
      return url;
    }
    if (!url.startsWith('s3://')) {
      return url;
    }
    const s3Parts = url.replace('s3://', '').split('/');
    const bucket = s3Parts[0];
    const key = s3Parts.slice(1).join('/');
    return `https://${bucket}.s3.eu-central-1.amazonaws.com/${key}`;
  }, []);

  const applySuperresUpdate = useCallback((jobImage, { jpgUrl, falseColorUrl, eviUrl, tifUrl }) => {
    if (!jobImage || !jpgUrl) {
      return;
    }
    const originalUrl = jobImage.superresolved ? jobImage.original_url : jobImage.url;
    const originalFalseColorUrl = jobImage.superresolved ? jobImage.original_false_color_url : jobImage.false_color_url;
    const originalEviUrl = jobImage.superresolved ? jobImage.original_evi_url : jobImage.evi_url;

    const resolvedFalseColorUrl =
      falseColorUrl ||
      (originalFalseColorUrl && jpgUrl ? jpgUrl.replace('.jpg', '_false_color.jpg') : jobImage.false_color_url);
    const resolvedEviUrl =
      eviUrl || (originalEviUrl && jpgUrl ? jpgUrl.replace('.jpg', '_evi.jpg') : jobImage.evi_url);

    const updatedImage = {
      ...jobImage,
      url: jpgUrl,
      false_color_url: resolvedFalseColorUrl,
      evi_url: resolvedEviUrl,
      original_url: originalUrl,
      original_false_color_url: originalFalseColorUrl,
      original_evi_url: originalEviUrl,
      superresolved: true,
      superres_tif_url: tifUrl,
      loading: false,
      lastUpdated: Date.now(),
    };

    setSelectedImage((prev) => {
      if (!prev || prev.path !== updatedImage.path) {
        return prev;
      }
      return {
        ...prev,
        ...updatedImage,
        center: prev.center,
        zoom: prev.zoom,
        bbox: prev.bbox,
        lastUpdated: Date.now(),
      };
    });

    setImages((prevImages) =>
      prevImages.map((img) => (img.path === updatedImage.path ? { ...img, ...updatedImage } : img))
    );
    setAllImages((prevImages) =>
      prevImages.map((img) => (img.path === updatedImage.path ? { ...img, ...updatedImage } : img))
    );

    // Deduct 1 credit only after SR image is successfully received
    deductOneCredit();
  }, [deductOneCredit]);

  // Apply SR overlays from history navigation (no credit deduction)
  useEffect(() => {
    if (!historySrTasks || historySrTasks.length === 0) return;
    const loadedImages = images.filter(img => img.url && !img.loading);
    if (loadedImages.length === 0) return;

    const srByDate = {};
    for (const sr of historySrTasks) {
      srByDate[sr.image_date] = sr;
    }

    const updatedImages = images.map(img => {
      if (img.loading || !img.url || img.superresolved) return img;
      const imgDate = img.date ? img.date.split('T')[0] : null;
      const sr = imgDate && srByDate[imgDate];
      if (!sr || !sr.preview_url) return img;

      return {
        ...img,
        original_url: img.url,
        original_false_color_url: img.false_color_url,
        original_evi_url: img.evi_url,
        url: sr.preview_url,
        false_color_url: sr.preview_url.replace('.jpg', '_false_color.jpg'),
        evi_url: sr.preview_url.replace('.jpg', '_evi.jpg'),
        superresolved: true,
      };
    });

    const anyApplied = updatedImages.some((img, i) => img.superresolved && !images[i].superresolved);
    if (anyApplied) {
      setImages(updatedImages);
      setAllImages(prev => {
        const byPath = {};
        updatedImages.forEach(img => { if (img.superresolved) byPath[img.path] = img; });
        return prev.map(img => byPath[img.path] || img);
      });
      setHistorySrTasks(null);
    }
  }, [historySrTasks, images]);

  // Auto-transition to imageview (map) after history images finish loading
  useEffect(() => {
    if (!historyAutoShowMap.current) return;
    if (!bbox || bbox.length < 4) return;
    const loadedImage = images.find(img => img.url && !img.loading);
    if (!loadedImage) return;

    historyAutoShowMap.current = false;

    // Compute center from bbox since map isn't rendered yet
    const center = { lng: (bbox[0] + bbox[2]) / 2, lat: (bbox[1] + bbox[3]) / 2 };
    const isMobile = window.innerWidth <= 768;
    const defaultZoom = isMobile ? 14.5 : 15.6;
    setMapCenter(center);
    setMapZoom(defaultZoom);

    const latestImage = pickLatestImage(images);
    if (latestImage) {
      const selectedImageData = {
        ...latestImage,
        bbox: bbox,
        center: center,
        zoom: defaultZoom,
      };
      setSelectedImage(selectedImageData);
      setInitialBbox(bbox);
      setInitialCenter(center);
      setInitialZoom(defaultZoom);
      setMode('imageview');
      navigate(buildPathWithQueryAndHash('/map'));
    }
  }, [images, bbox, pickLatestImage, navigate, buildPathWithQueryAndHash]);

  const startMagicZoomJob = useCallback(
    (job) => {
      const run = async () => {
        if (!job || !job.image || !job.bbox) {
          setActiveMagicZoom(null);
          return;
        }

        const jobImage = job.image;
        const bboxArray = job.bbox;
        const bboxString = bboxArray.join(',');
        const date = jobImage.date.split('T')[0];

        setActiveMagicZoom(job);
        setMagicZoomProgress(5);
        setMagicZoomMessage('Starting...');

        const areaSqm = boxDimensions ? boxDimensions.width * boxDimensions.height : 250000; // default 500x500m
        Analytics.trackMagicZoomInitiated(
          bboxArray,
          date,
          areaSqm,
          jobImage.percentages?.cloud_percentage
        );

        const startTime = Date.now();
        try {
          await refreshTokensIfNeeded();
        } catch (error) {
          const resumeImage = buildPendingMagicZoomImage({
            ...job.image,
            bbox: job.bbox || job.image?.bbox,
            mgrs_tile: job.mgrs_tile || job.image?.mgrs_tile,
            stacked_tif_s3_path: job.stackedTifPath || job.image?.stacked_tif_s3_path
          });
          if (resumeImage) {
            storePendingMagicZoom({ type: 'single', image: resumeImage });
          }
          setMagicZoomMessage('Sign in required.');
          setAuthError('Please sign in again to start AI Zoom.');
          setShowAuthModal(true);
          setActiveMagicZoom(null);
          return;
        }

        const authHeader = getAuthorizationHeader();
        const requestHeaders = {
          ...createSessionHeaders(),
          'X-GA-Cookies': document.cookie,
          ...(authHeader ? { Authorization: authHeader } : {})
        };
      const mgrsParam = job.mgrs_tile ? `&mgrs_tile=${job.mgrs_tile}` : '';
      const stackedTifParam = job.stackedTifPath
        ? `&stacked_tif_s3_path=${encodeURIComponent(job.stackedTifPath)}`
        : '';

      const startProgressPolling = (taskId) => {
        let progressInterval;
        let smoothProgressInterval;
        let targetProgress = 10; // Start with 10% target
        let currentProgress = 5; // Start from 5% (where we left off)
        let fulfilled = false; // Guard against duplicate COMPLETED from concurrent polls

        const stopPolling = () => {
          clearInterval(progressInterval);
          clearInterval(smoothProgressInterval);
        };

        const finalizeJob = (success, queueWaitTime = 0) => {
          const processingTime = Date.now() - startTime;
          const center = [(bboxArray[0] + bboxArray[2]) / 2, (bboxArray[1] + bboxArray[3]) / 2];
          Analytics.trackMagicZoomCompleted(
            success,
            processingTime,
            queueWaitTime,
            `${center[1]},${center[0]}`
          );
          setActiveMagicZoom(null);
        };

        const startSmoothProgress = () => {
          smoothProgressInterval = setInterval(() => {
            if (currentProgress < targetProgress) {
              currentProgress = Math.min(currentProgress + 0.5, targetProgress);
              setMagicZoomProgress(currentProgress);
            }
          }, 50); // Update every 50ms for smooth animation
        };

        startSmoothProgress();
        progressInterval = setInterval(() => {
          fetch(`${apiConfig.checkS2x10Progress}?task_id=${taskId}`, {
            method: 'GET',
            credentials: 'omit',
            headers: requestHeaders
          })
            .then((response) => {
              if (response.status === 401 || response.status === 403) {
                stopPolling();
                setMagicZoomMessage('Sign in required.');
                setAuthError('Please sign in again to check progress.');
                setShowAuthModal(true);
                setActiveMagicZoom(null);
                return null;
              }
              return response.json();
            })
            .then((data) => {
              if (!data) {
                return;
              }
              console.log('Progress update:', data);
              console.log(`[Progress ${new Date().toLocaleTimeString()}] Status: ${data.status}, Message: ${data.message || 'N/A'}, Queue: ${data.queue_position || 'N/A'}`);
              
              // Update progress based on status
              if (data.status === 'QUEUED') {
                const queueProgress = data.queue_position ? Math.max(5, 20 - data.queue_position * 5) : 10;
                targetProgress = queueProgress;
                const queueMessage = data.queue_position 
                  ? `Waiting in queue (position ${data.queue_position})`
                  : 'Request queued for processing';
                setMagicZoomMessage(queueMessage);
                
                // Track queue position
                if (data.queue_position) {
                  Analytics.trackQueuePosition(data.queue_position, data.queue_length || data.queue_position);
                }
              } else if (data.status === 'PREPROCESSING_S2_DATA') {
                targetProgress = 30;
                setMagicZoomMessage(data.message || 'Preprocessing satellite data...');
              } else if (data.status === 'INFERRING') {
                targetProgress = 60;
                setMagicZoomMessage(data.message || 'Running super-resolution model...');
              } else if (data.status === 'POSTPROCESSING_RESULTS') {
                targetProgress = 85;
                setMagicZoomMessage(data.message || 'Assembling enhanced image...');
              } else if (data.status === 'PROCESSING') {
                // Generic processing status
                const baseProgress = 30;
                const stageProgress = data.processing_stage ? 30 : 0;
                const elapsedProgress = data.elapsed_time ? Math.min(30, data.elapsed_time / 2) : 0;
                targetProgress = baseProgress + stageProgress + elapsedProgress;
                setMagicZoomMessage(data.processing_stage || data.message || 'Processing satellite imagery...');
              } else if (data.status === 'COMPLETED') {
                stopPolling();
                if (fulfilled) return;
                fulfilled = true;
                targetProgress = 100;
                setMagicZoomProgress(100);
                setMagicZoomMessage('Enhancement complete!');

                const jpgUrl = normalizeS3Url(data.outputs?.preview_url);
                const tifUrl = normalizeS3Url(data.outputs?.superres_url);
                if (jpgUrl) {
                  applySuperresUpdate(jobImage, { jpgUrl, tifUrl });
                  finalizeJob(true, data.queue_wait_time || 0);
                } else {
                  console.error('No JPG URL in progress outputs:', data.outputs);
                  setMagicZoomMessage('Error: Image URL not found');
                  finalizeJob(false);
                }
              } else if (data.status === 'FAILED' || data.status === 'ERROR') {
                stopPolling();
                if (fulfilled) return;
                fulfilled = true;
                console.error('Task failed:', data.error_message);
                setMagicZoomMessage('Enhancement failed. Please try again.');
                finalizeJob(false);
              } else if (data.status === 'NOT_FOUND') {
                // Task not found, might still be initializing
                setMagicZoomMessage('Initializing enhancement...');
              }
            })
            .catch((error) => {
              console.error('Error checking progress:', error);
              // Don't stop polling on error, just log it
            });
        }, 1000); // Poll every second
      };

      fetch(
        `${apiConfig.getS2x10}?bbox=${bboxString}&date=${date}${mgrsParam}${stackedTifParam}&async=true`,
        {
          method: 'GET',
          credentials: 'omit',
          headers: requestHeaders
        }
      )
        .then((response) => {
          if (response.status === 401 || response.status === 403) {
            const resumeImage = buildPendingMagicZoomImage({
              ...jobImage,
              bbox: bboxArray,
              mgrs_tile: job.mgrs_tile || jobImage?.mgrs_tile,
              stacked_tif_s3_path: job.stackedTifPath || jobImage?.stacked_tif_s3_path
            });
            if (resumeImage) {
              storePendingMagicZoom({ type: 'single', image: resumeImage });
            }
            setMagicZoomMessage('Sign in required.');
            setAuthError('Please sign in to start AI Zoom.');
            setShowAuthModal(true);
            setActiveMagicZoom(null);
            return null;
          }
          return response.json();
        })
        .then((data) => {
          if (!data) {
            return;
          }
          console.log('Magic Zoom API response:', data);
          console.log('Preview JPG URL:', data.preview_jpg);
          console.log('API Response URL_MS field:', data.URL_MS);
          console.log('API Response full data:', JSON.stringify(data, null, 2));
          
          if (data.status === 'QUEUED' || data.status === 'PROCESSING') {
            // Task is queued or processing, start polling
            startProgressPolling(data.task_id);
          } else if (data.preview_jpg) {
            const jpgUrl = normalizeS3Url(data.preview_jpg);
            const tifUrl = normalizeS3Url(data.URL_MS);
            const falseColorUrl = normalizeS3Url(data.false_color_jpg);
            const eviUrl = normalizeS3Url(data.evi_jpg);

            setMagicZoomMessage('Enhancement complete!');
            setMagicZoomProgress(100);
            applySuperresUpdate(jobImage, { jpgUrl, falseColorUrl, eviUrl, tifUrl });

            const processingTime = Date.now() - startTime;
            const center = [(bboxArray[0] + bboxArray[2]) / 2, (bboxArray[1] + bboxArray[3]) / 2];
            Analytics.trackMagicZoomCompleted(
              true,
              processingTime,
              0,
              `${center[1]},${center[0]}`
            );

            setActiveMagicZoom(null);
          } else {
            console.error('Unexpected response from API:', data);
            setMagicZoomMessage('Enhancement failed. Please try again.');
            const processingTime = Date.now() - startTime;
            const center = [(bboxArray[0] + bboxArray[2]) / 2, (bboxArray[1] + bboxArray[3]) / 2];
            Analytics.trackMagicZoomCompleted(
              false,
              processingTime,
              0,
              `${center[1]},${center[0]}`
            );
            setActiveMagicZoom(null);
          }
        })
        .catch((error) => {
          console.error('Error during Magic Zoom API call:', error);
          
          // Track API error
          Analytics.trackApiError('getS2x10', error.message || 'fetch_failed', `initial request for ${date}`);
          
          // Track failed completion
          const processingTime = Date.now() - startTime;
          const center = [(bboxArray[0] + bboxArray[2]) / 2, (bboxArray[1] + bboxArray[3]) / 2];
          Analytics.trackMagicZoomCompleted(
            false,
            processingTime,
            0,
            `${center[1]},${center[0]}`
          );
          
          setActiveMagicZoom(null);
        });
      };

      run();
    },
    [applySuperresUpdate, boxDimensions, buildPendingMagicZoomImage, normalizeS3Url, storePendingMagicZoom]
  );

  const enqueueMagicZoomJobs = useCallback(
    (jobImages, { trackBatch = false } = {}) => {
      if (!jobImages || jobImages.length === 0) {
        return;
      }
      setMagicZoomQueue((prevQueue) => {
        const queuedPaths = new Set(prevQueue.map((job) => job.image?.path));
        if (activeMagicZoom?.image?.path) {
          queuedPaths.add(activeMagicZoom.image.path);
        }
        const nextQueue = [...prevQueue];
        let addedCount = 0;
        jobImages.forEach((image) => {
          if (!image || queuedPaths.has(image.path)) {
            return;
          }
          const jobBbox = image.bbox || bbox;
          if (!jobBbox || jobBbox.length !== 4) {
            return;
          }
          const job = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            image: { ...image, bbox: jobBbox },
            bbox: jobBbox,
            mgrs_tile: image.mgrs_tile || '',
            stackedTifPath: image.stacked_tif_s3_path || ''
          };
          queuedPaths.add(image.path);
          nextQueue.push(job);
          addedCount += 1;
        });
        if (addedCount > 0) {
          setMagicZoomBatchTotal((prevTotal) => {
            if (!trackBatch && prevTotal === 0) {
              return prevTotal;
            }
            const baseTotal =
              prevTotal > 0
                ? prevTotal
                : prevQueue.length + (activeMagicZoom?.image?.path ? 1 : 0);
            return baseTotal + addedCount;
          });
        }
        return nextQueue;
      });
    },
    [activeMagicZoom, bbox]
  );

  useEffect(() => {
    if (!pendingMagicZoom) {
      return;
    }
    if (!isAuthenticated()) {
      return;
    }
    if (pendingMagicZoom.type === 'all') {
      if (!timelineImages || timelineImages.length === 0) {
        return;
      }
      enqueueMagicZoomJobs(timelineImages, { trackBatch: true });
      clearPendingMagicZoom();
      return;
    }
    if (pendingMagicZoom.type === 'single') {
      if (!pendingMagicZoom.image) {
        clearPendingMagicZoom();
        return;
      }
      enqueueMagicZoomJobs([pendingMagicZoom.image]);
      clearPendingMagicZoom();
    }
  }, [
    authUser,
    clearPendingMagicZoom,
    enqueueMagicZoomJobs,
    pendingMagicZoom,
    timelineImages
  ]);

  const handleMagicZoomClick = () => {
    if (!selectedImage) {
      console.error('No image selected for Magic Zoom');
      return;
    }
    if (!requireMagicZoomAuth()) {
      queueMagicZoomAfterAuth('single', selectedImage);
      return;
    }
    requireCredits(1, () => enqueueMagicZoomJobs([selectedImage]));
  };

  const handleMagicZoomAllClick = () => {
    if (!requireMagicZoomAuth()) {
      queueMagicZoomAfterAuth('all');
      return;
    }
    const needsSR = timelineImages.filter((img) => !img.superresolved);
    if (needsSR.length === 0) {
      return;
    }
    requireCredits(needsSR.length, () => enqueueMagicZoomJobs(needsSR, { trackBatch: true }));
  };

  const handleDownloadGif = useCallback(async () => {
    if (isGifGenerating) {
      return;
    }

    const sourceImages = timelineImages
      .filter((image) => !image.loading)
      .map((image) => ({ image, url: getImageUrlForMode(image) }))
      .filter((entry) => entry.url);

    if (sourceImages.length === 0) {
      console.error('No images available to build GIF.');
      return;
    }

    const sorted = sourceImages.sort(
      (a, b) => new Date(a.image.date) - new Date(b.image.date)
    );

    const loadImage = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
      });

    setIsGifGenerating(true);
    setGifProgress(0);

    try {
      const hasSuperresolved = sorted.some((entry) => entry.image?.superresolved);
      const baseEntry = hasSuperresolved
        ? sorted.find((entry) => entry.image?.superresolved) || sorted[0]
        : sorted[0];
      const imageCache = new Map();
      const baseImg = await loadImage(baseEntry.url);
      imageCache.set(baseEntry.url, baseImg);

      const scaleFactor = hasSuperresolved ? 1 : 10;
      const baseWidth = Math.round((baseImg.width || 0) * scaleFactor);
      const baseHeight = Math.round((baseImg.height || 0) * scaleFactor);
      if (!baseWidth || !baseHeight) {
        throw new Error('Invalid image dimensions for GIF.');
      }

      const canvas = document.createElement('canvas');
      canvas.width = baseWidth;
      canvas.height = baseHeight;
      const ctx = canvas.getContext('2d');

      const gif = new GIF({
        workers: 2,
        quality: 10,
        workerScript: GIF_WORKER_URL,
        width: baseWidth,
        height: baseHeight
      });

      gif.on('progress', (progress) => {
        setGifProgress(Math.round(progress * 100));
      });

      gif.on('finished', (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const firstDate = sorted[0].image.date.slice(0, 10);
        const lastDate = sorted[sorted.length - 1].image.date.slice(0, 10);
        const fileName = `stm_${firstDate}_${lastDate}.gif`;
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        logFrontendEvent({
          endpoint: '/download-gif',
          method: 'GET',
          requestParams: {
            first_date: firstDate,
            last_date: lastDate,
            frame_count: sorted.length,
            has_superresolved: hasSuperresolved,
            image_mode: imageViewMode,
            file_name: fileName,
          },
          bbox,
          imageDate: firstDate,
        });
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        setIsGifGenerating(false);
        setGifProgress(0);
      });

      const drawFrame = (imageEntry, imgEl) => {
        ctx.clearRect(0, 0, baseWidth, baseHeight);
        ctx.drawImage(imgEl, 0, 0, baseWidth, baseHeight);

        const dateText = imageEntry.image.date
          ? imageEntry.image.date.slice(0, 10)
          : '';
        const coords = resolveLatLon(imageEntry.image);
        const coordText = coords
          ? `Lat ${coords.lat.toFixed(5)}  Lon ${coords.lon.toFixed(5)}`
          : '';
        const lines = coordText ? [dateText, coordText] : [dateText];

        const coordFontSize = Math.max(14, Math.round(baseWidth * 0.026));
        const baseDateFontSize = Math.max(
          coordFontSize + 4,
          Math.round(coordFontSize * 1.25)
        );
        const dateFontSize = Math.round(baseDateFontSize * 1.25);
        ctx.textBaseline = 'top';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';

        const lineMetrics = lines.map((line, index) => {
          const fontSize = index === 0 ? dateFontSize : coordFontSize;
          ctx.font = `${fontSize}px 'PT Sans Narrow', Arial, sans-serif`;
          return {
            text: line,
            fontSize,
            width: ctx.measureText(line).width,
            lineHeight: Math.round(fontSize * 1.2)
          };
        });

        const padding = Math.max(6, Math.round(coordFontSize * 0.4));
        const textWidth = Math.max(...lineMetrics.map((metric) => metric.width));
        const boxWidth = textWidth + padding * 2;
        const boxHeight =
          lineMetrics.reduce((sum, metric) => sum + metric.lineHeight, 0) +
          padding * 2;
        const boxX = 10;
        const boxY = baseHeight - boxHeight - 10;

        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';

        let currentY = boxY + padding;
        lineMetrics.forEach((metric) => {
          ctx.font = `${metric.fontSize}px 'PT Sans Narrow', Arial, sans-serif`;
          ctx.fillText(metric.text, boxX + padding, currentY);
          currentY += metric.lineHeight;
        });

        gif.addFrame(canvas, { copy: true, delay: 700 });
      };

      for (let i = 0; i < sorted.length; i += 1) {
        try {
          const entry = sorted[i];
          const imgEl = imageCache.get(entry.url) || (await loadImage(entry.url));
          imageCache.set(entry.url, imgEl);
          drawFrame(entry, imgEl);
        } catch (error) {
          console.warn('Skipping GIF frame due to load error:', error);
        }
      }

      gif.render();
    } catch (error) {
      console.error('Failed to build GIF:', error);
      setIsGifGenerating(false);
      setGifProgress(0);
    }
  }, [bbox, getImageUrlForMode, imageViewMode, isGifGenerating, resolveLatLon, timelineImages]);

  useEffect(() => {
    if (activeMagicZoom || magicZoomQueue.length === 0) {
      return;
    }
    const [nextJob, ...rest] = magicZoomQueue;
    setMagicZoomQueue(rest);
    startMagicZoomJob(nextJob);
  }, [activeMagicZoom, magicZoomQueue, startMagicZoomJob]);

  useEffect(() => {
    if (!activeMagicZoom && magicZoomQueue.length === 0) {
      setMagicZoomProgress(0);
      setMagicZoomMessage('');
      setMagicZoomBatchTotal(0);
    }
  }, [activeMagicZoom, magicZoomQueue.length]);

  return (
    <div className="app-container">
      <TopBar
        onAboutClick={showAbout ? handleCloseAbout : handleAboutClick}
        mode={mode}
        showAbout={showAbout}
        authUser={authUser}
        authConfigured={authConfigured}
        onAuthClick={() => {
          setAuthError('');
          setAuthStep('email');
          setAuthCode('');
          setShowAuthModal(true);
        }}
        onSignOut={handleSignOut}
        creditBalance={creditBalance}
        onBuyCredits={handleBuyCredits}
      />
      {showAbout && <AboutPopup onClose={handleCloseAbout} />}
      <CreditModal
        isOpen={showCreditModal}
        balance={creditBalance}
        requiredCredits={creditModalRequired}
        loading={creditModalLoading}
        errorMessage={creditModalError}
        onProceed={handleCreditModalProceed}
        onBuyCredits={handleBuyCredits}
        onClose={handleCreditModalClose}
      />
      <AuthModal
        isOpen={showAuthModal}
        emailConfigured={emailAuthConfigured}
        googleConfigured={googleAuthConfigured}
        step={authStep}
        email={authEmail}
        code={authCode}
        loading={authLoading}
        errorMessage={authError}
        onClose={() => setShowAuthModal(false)}
        onEmailChange={setAuthEmail}
        onCodeChange={setAuthCode}
        onSendCode={handleEmailAuthStart}
        onVerifyCode={handleEmailAuthVerify}
        onResendCode={handleEmailAuthStart}
        onBack={handleEmailAuthReset}
        onGoogleSignIn={() => handleAuthStart('Google')}
      />
      <MapComponent
        setBbox={setBbox}
        selectedImage={selectedImage}
        mapCenter={mapCenter}
        mapZoom={mapZoom}
        setMapCenter={setMapCenter}
        setMapZoom={setMapZoom}
        showRedBox={showRedBox}
        setBoxDimensionsForImages={setBoxDimensions}
        onMapMove={handleMapMove}
        imageViewMode={imageViewMode}
        onToggleImageView={handleToggleImageView}
        tileMode={tileMode}
        onToggleTileMode={handleToggleTileMode}
        onRequestBboxUpdate={setUpdateBboxFn}
        onRequestMapState={handleSetMapStateFn}
        mode={mode}
      />
      <div className={`toggle-images-button ${mode}`}>
        {mode === 'map' && (
          /* Map mode: All buttons grouped in center */
          <div className="center-group">
            <button className="button" onClick={handleButtonClick}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              {buttonText}
            </button>
          </div>
        )}
        
        {mode === 'imagelist' && (
          <div className="imagelist-controls">
            {hasSelection && (
              <button className="button light-grey-button" onClick={handleClearSelection}>
                Clear selection
              </button>
            )}
            <button className="button" onClick={handleButtonClick}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {buttonText}
            </button>
            {hasImages && (
              <>
                <button
                  className="button white-button"
                  onClick={handleRemoveCloudyImagesClick}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>
                  {cloudThresholdLabels[cloudFilterIndex]}
                </button>
                  <button
                    className={`button ${imageViewMode === 'natural' ? 'orange-button' : imageViewMode === 'evi' ? 'green-button' : 'red-button'}`}
                    onClick={handleToggleImageView}
                    title="Cycle through image view modes"
                  >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><circle cx="12" cy="12" r="10"/><path d="M12 2a15 15 0 014 10 15 15 0 01-4 10 15 15 0 01-4-10 15 15 0 014-10z"/></svg>
                  {imageViewMode === 'natural' ? 'Natural Color' : imageViewMode === 'evi' ? 'EVI' : 'False Color'}
                </button>
              </>
            )}
          </div>
        )}
        
        {mode === 'imageview' && (
          /* ImageView mode: Grouped in center */
          <div className="center-group">
            <button className="button" onClick={handleViewImageListClick}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              Image list
            </button>
            <button
              className="button magic-zoom-button"
              onClick={handleMagicZoomClick}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: '-2px', marginRight: 4}}><path d="M12 2l2.09 6.26L20.18 9l-5 4.09L16.82 20 12 16.54 7.18 20l1.64-6.91L3.82 9l6.09-.74z"/></svg>
              AI Zoom (1 credit)
            </button>
            {zoomAllEnabled && (
              <button
                className="button magic-zoom-button"
                onClick={handleMagicZoomAllClick}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: '-2px', marginRight: 4}}><path d="M9.5 1l1.2 3.6L14.3 5.3l-2.8 2.3.9 3.9L9.5 9.4 6.6 11.5l.9-3.9-2.8-2.3 3.6-.7z"/><path d="M19 8l.8 2.4 2.4.5-1.9 1.5.6 2.6-2.0-1.4-2.0 1.4.6-2.6-1.9-1.5 2.4-.5z" opacity=".7"/><path d="M15 16l.6 1.8 1.8.4-1.4 1.1.5 2-1.5-1.1-1.5 1.1.5-2-1.4-1.1 1.8-.4z" opacity=".5"/></svg>
                AI Zoom All ({timelineImages.filter(img => !img.superresolved).length} credits)
              </button>
            )}
            <button className="button" onClick={handleButtonClick}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {buttonText}
            </button>
          </div>
        )}
      </div>
      {showMagicZoomBar && (
        <div className="magic-zoom-bar">
          <div className="magic-zoom-bar-header">
            <span className="magic-zoom-bar-title">AI Zoom</span>
            {magicZoomActiveDate ? (
              <span className="magic-zoom-bar-date">{magicZoomActiveDate}</span>
            ) : (
              magicZoomNextDate && <span className="magic-zoom-bar-date">Next: {magicZoomNextDate}</span>
            )}
            <span className="magic-zoom-bar-queue">Queue: {magicZoomQueueCount}</span>
            {magicZoomDisplayProgress > 0 && (
              <span className="magic-zoom-bar-progress">{Math.round(magicZoomDisplayProgress)}%</span>
            )}
          </div>
          <div className="magic-zoom-bar-track">
            <div
              className="magic-zoom-bar-fill"
              style={{ width: `${Math.min(100, Math.max(0, magicZoomDisplayProgress))}%` }}
            ></div>
          </div>
          <div className="magic-zoom-bar-status">
            {activeMagicZoom
              ? magicZoomMessage || 'Processing...'
              : 'Waiting to start...'}
          </div>
        </div>
      )}
      {mode === 'map' && showSearch && (
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search location..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                
                // Debounce search suggestions
                if (searchTimeout) {
                  clearTimeout(searchTimeout);
                }
                const newTimeout = setTimeout(() => {
                  fetchSearchSuggestions(e.target.value);
                }, 300);
                setSearchTimeout(newTimeout);
              }}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                // Hide suggestions after a short delay to allow clicking
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              onFocus={() => {
                if (searchSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
            />
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="search-suggestions">
                {searchSuggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.id || index}
                    className={`search-suggestion ${
                      index === selectedSuggestionIndex ? 'selected' : ''
                    }`}
                    onClick={() => handleSuggestionSelect(suggestion)}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                  >
                    <div className="suggestion-text">{suggestion.place_name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            className="search-button"
            onClick={handleSearch}
            disabled={!searchQuery.trim() || searchLoading}
            title="Search location"
          >
            {searchLoading ? '⏳' : '🔍'}
          </button>
        </div>
      )}
      {mode === 'imageview' && (
        <button
          className="button map-gif-button"
          onClick={handleDownloadGif}
          disabled={isGifGenerating || !hasGifFrames}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: '-2px', marginRight: 4}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {isGifGenerating
            ? `Preparing GIF ${gifProgress ? `${gifProgress}%` : ''}`.trim()
            : 'Download GIF'}
        </button>
      )}
      {mode === 'imagelist' && bbox && (
        <ImageList
          bbox={bbox}
          onImageClick={handleToggleImageSelection}
          updateImages={updateImages}
          images={images}
          allImages={allImages}
          selectedImagePaths={selectedImagePaths}
          boxDimensions={boxDimensions}
          imageViewMode={imageViewMode}
          onToggleImageView={handleToggleImageView}
          loadingBatch={loadingImageBatch}
          useGlobalImageLoader
        />
      )}
      {mode === 'imageview' && timelineImages.length > 0 && (
        <ThumbnailBar
          images={timelineImages}
          selectedImage={selectedImage}
          onImageSelect={handleDateChange}
        />
      )}
      {mode === 'map' && <PoweredByBar />}
      {showFirstVisitPopup && <FirstVisitPopup onClose={handleCloseFirstVisitPopup} />} 
    </div>
  );
};

export default App;// Dev deployment trigger
